// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Tag } from '@imprivata-cloud/design-system';
import type { TableColumnsType } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { useTranslation } from 'react-i18next';
import { tagColor } from './utils/endpointGroups';
import type { EndpointRow } from './utils/toEndpointRow';

const TRANSLATION_ROOT = 'portal.settings.endpoints';
const VERSION_COLUMN_WIDTH = 160;
const SELECTION_COLUMN_WIDTH = '3rem';

export function buildColumns(
  t: ReturnType<typeof useTranslation>['t'],
): TableColumnsType<EndpointRow> {
  return [
    {
      title: t(`${TRANSLATION_ROOT}.columns.hostname`),
      dataIndex: 'hostname',
      key: 'hostname',
      width: 320,
      render: (hostname: string) => <span className="endpoints-hostname">{hostname}</span>,
    },
    {
      // Figma stacks agent version (secondary color) over the installation date
      // (primary color) in a single column.
      title: t(`${TRANSLATION_ROOT}.columns.version`),
      key: 'version-installed',
      // Figma keeps the group Tag right after the stacked version, not at the far edge.
      width: VERSION_COLUMN_WIDTH,
      render: (_: unknown, row: EndpointRow) => (
        <div className="endpoints-version-installed">
          <span className="endpoints-version">{row.version}</span>
          <span className="endpoints-installed-date">{row.installedDate}</span>
        </div>
      ),
    },
    {
      key: 'group',
      render: (_: unknown, row: EndpointRow) =>
        row.group && (
          // Tag takes no title, so the wrapper carries the full name for truncated tags.
          <span className="endpoints-group-tag" title={row.group.displayName}>
            <Tag text={row.group.displayName} color={tagColor(row.group.groupId)} />
          </span>
        ),
    },
  ];
}

export function buildRowSelection(
  t: ReturnType<typeof useTranslation>['t'],
  selected: readonly string[],
  onChange: (endpointUuids: string[]) => void,
): TableRowSelection<EndpointRow> {
  return {
    // With `.ant-table-selection-column` in Endpoints.less, lines the row checkboxes up under the
    // toolbar's select-all and the hostnames under the search box.
    columnWidth: SELECTION_COLUMN_WIDTH,
    selectedRowKeys: [...selected],
    onChange: keys => onChange(keys.filter((k): k is string => typeof k === 'string')),
    // antd types no aria-label here; `title` reaches the input and names the checkbox.
    getCheckboxProps: row => ({
      title: t(`${TRANSLATION_ROOT}.selection.select-row`, { name: row.hostname }),
    }),
  };
}
